import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { LockKeyhole, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { mainNav } from '@/constants/site'

interface SiteHeaderProps {
    // Let the header sit see-through over a full-bleed hero until the visitor scrolls
    overHero?: boolean
}

/*
   Ultra-modern header
   Left: wordmark. Centre: section links. Right: the discreet Resident Access
   entry point (lock icon, hairline pill) next to the primary viewing CTA.
   It turns into frosted cream glass once the page scrolls.
*/
export const SiteHeader: FC<SiteHeaderProps> = ({ overHero = false }): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // Lock page scroll and listen for Escape while the mobile menu is open
    useEffect(() => {
        if (!menuOpen) return

        const previous = document.body.style.overflow
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [menuOpen])

    // Close the mobile menu whenever the visitor navigates
    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    const solid = !overHero || scrolled || menuOpen

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,color] duration-500',
                    solid
                        ? 'border-charcoal/10 bg-cream/85 text-charcoal backdrop-blur-xl backdrop-saturate-150'
                        : 'border-transparent bg-transparent text-cream'
                )}
            >
                <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-6 lg:px-10'>
                    <Link
                        href='/'
                        aria-label='Rockwood Estate, home'
                        className='rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-4'
                    >
                        <Logo tone={solid ? 'dark' : 'light'} />
                    </Link>

                    <nav aria-label='Main' className='hidden lg:block'>
                        <ul className='flex items-center gap-9'>
                            {mainNav.map(link => (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        className={cn(
                                            'link-underline pb-1 text-[0.8125rem] font-medium tracking-[0.02em] transition-colors duration-300',
                                            solid ? 'text-charcoal/75 hover:text-charcoal' : 'text-cream/85 hover:text-cream'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2 sm:gap-3'>
                        {/* Discreet, secure entry point for residents */}
                        <Link
                            href='/residents'
                            aria-label='Resident Access, sign in to the resident portal'
                            className={cn(
                                'group inline-flex h-9 items-center gap-2 rounded-full border px-3 text-[0.6875rem] font-medium uppercase tracking-[0.18em] transition-colors duration-300 sm:px-3.5',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2',
                                solid
                                    ? 'border-charcoal/15 text-charcoal/75 hover:border-forest hover:text-forest'
                                    : 'border-cream/30 text-cream/85 hover:border-cream hover:text-cream'
                            )}
                        >
                            <LockKeyhole aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover:-translate-y-px' />

                            <span className='hidden sm:inline'>
                                Resident Access
                            </span>
                        </Link>

                        <ButtonLink
                            href='/#enquire'
                            size='sm'
                            variant={solid ? 'primary' : 'light'}
                            className='hidden md:inline-flex'
                        >
                            Book a viewing
                        </ButtonLink>

                        <button
                            type='button'
                            onClick={() => setMenuOpen(open => !open)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            className={cn(
                                'inline-flex size-10 items-center justify-center rounded-full transition-colors lg:hidden',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest',
                                solid ? 'hover:bg-charcoal/5' : 'hover:bg-cream/10'
                            )}
                        >
                            {menuOpen ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}
                        </button>
                    </div>
                </div>
            </header>

            {/*
               Mobile and tablet menu: a calm full-height panel below the bar.
               It sits outside the header because the header's frosted glass
               (backdrop-filter) would otherwise trap a fixed panel inside it.
            */}
            <div
                id='mobile-menu'
                hidden={!menuOpen}
                className='fixed inset-x-0 bottom-0 top-20 z-40 overflow-y-auto border-t border-charcoal/10 bg-cream text-charcoal lg:hidden'
            >
                <nav aria-label='Mobile' className='mx-auto flex min-h-full max-w-7xl flex-col px-6 pb-10 pt-8 animate-in fade-in slide-in-from-top-2 duration-300'>
                    <ul className='flex flex-col divide-y divide-charcoal/10 border-y border-charcoal/10'>
                        {mainNav.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setMenuOpen(false)}
                                    className='flex items-center justify-between py-5 font-heading text-3xl transition-colors hover:text-forest'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <div className='mt-10 flex flex-col gap-3'>
                        <ButtonLink href='/#enquire' size='lg' onClick={() => setMenuOpen(false)}>
                            Book a private viewing
                        </ButtonLink>

                        <ButtonLink href='/residents' size='lg' variant='outline'>
                            <LockKeyhole aria-hidden='true' className='size-4' />
                            Resident Access
                        </ButtonLink>
                    </div>

                    <p className='mt-auto pt-10 text-xs uppercase tracking-[0.3em] text-muted-foreground'>
                        Hillcrest · KwaZulu-Natal
                    </p>
                </nav>
            </div>
        </>
    )
}
